export class UTCDate {
  private readonly year: number;
  private readonly month: number; // 0-based
  private readonly date: number; // day-of-month 1..31
  private readonly epochMillis: number; // cached UTC midnight millis

  private constructor(year: number, month: number, date: number) {
    // Special logic to allow class-transformer to create an empty instance via new UTCDate()
    if (arguments.length === 0) {
      this.year = 0;
      this.month = 0;
      this.date = 1;
      this.epochMillis = Date.UTC(0, 0, 1);
      return;
    }
    // Accept only integers within plausible ranges; year range left broad (0..9999) to match 4-digit formatting
    if (!Number.isInteger(year) || year < 0 || year > 9999) throw new Error(`Invalid year: ${year}`);
    if (!Number.isInteger(month) || month < 0 || month > 11) throw new Error(`Invalid month (0-11 expected): ${month}`);
    if (!Number.isInteger(date) || date < 1 || date > 31) throw new Error(`Invalid day-of-month: ${date}`);

    const dim = UTCDate.daysInMonth(year, month);
    if (date > dim) throw new Error(`Invalid day-of-month: ${year}-${month + 1}-${date} (month has ${dim} days)`);

    this.year = year;
    this.month = month;
    this.date = date;
    this.epochMillis = Date.UTC(year, month, date);
    Object.freeze(this); // enforce immutability
  }

  /**
   * Create from parts (monthIndex 0-based like Date.UTC) with native month AND day overflow / underflow normalization.
   * Behavior mirrors new Date(Date.UTC(year, monthIndex, day)).
   * Examples:
   *  fromYMD(2025, 12, 10) -> 2026-01-10 (month overflow)
   *  fromYMD(2025, -1, 10) -> 2024-12-10 (month underflow)
   *  fromYMD(2025, 3, 31)  -> 2025-05-01 (April 31 -> May 1)
   *  fromYMD(2025, 1, 30)  -> 2025-03-02 (Feb 30 -> Mar 2, non‑leap year)
   *  fromYMD(2025, 1, 29)  -> 2025-03-01 (Feb 29 -> Mar 1, non‑leap year)
   * Validation: only that inputs are integers and the normalized year remains within 0..9999. Day/month overflow never throws here.
   * For strict validation (rejecting invalid day-of-month) use UTCDate.parse("YYYY-MM-DD") instead.
   */
  static fromYMD(year: number, monthIndex: number, day: number): UTCDate {
    if (!Number.isInteger(year)) throw new Error(`Invalid year: ${year}`);
    if (!Number.isInteger(monthIndex)) throw new Error(`Invalid month index: ${monthIndex}`);
    if (!Number.isInteger(day)) throw new Error(`Invalid day-of-month: ${day}`);

    const provisional = new Date(Date.UTC(year, monthIndex, day));
    const normYear = provisional.getUTCFullYear();
    const normMonth = provisional.getUTCMonth();
    const normDay = provisional.getUTCDate();
    if (normYear < 0 || normYear > 9999) throw new Error(`Invalid year after normalization: ${normYear}`);
    return new UTCDate(normYear, normMonth, normDay);
  }

  /** Create from a native Date (UTC components, truncates to midnight) */
  static fromDate(d: Date): UTCDate {
    return new UTCDate(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }

  /** Create from the current date (UTC components, truncates to midnight) */
  static fromNow(): UTCDate {
    const now = new Date();
    return new UTCDate(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  }

  /**
   * Canonical parse entry point. Accepts either 'YYYY-MM-DD' or a strict midnight ISO string.
   * Preferred over fromString / parseISODate / fromISODate.
   */
  static parse(s: string): UTCDate {
    if (/^\d{4}-\d{2}-\d{2}$/.test(s)) {
      const [yStr, mStr, dStr] = s.split("-");
      const y = Number(yStr);
      const m = Number(mStr);
      const d = Number(dStr);
      if (m < 1 || m > 12) throw new Error(`Invalid month (1-12 expected): ${m}`);
      const dim = UTCDate.daysInMonth(y, m - 1);
      if (d < 1 || d > dim) throw new Error(`Invalid day-of-month: ${y}-${m}-${d} (month has ${dim} days)`);
      return UTCDate.fromYMD(y, m - 1, d);
    }
    const mIso = /^(\d{4})-(\d{2})-(\d{2})T(\d{2})(?::(\d{2})(?::(\d{2})(?:\.(\d{1,9}))?)?)?Z$/.exec(s);
    if (!mIso) throw new Error(`Invalid UTCDate string: ${s}`);
    const [, yStr, MStr, dStr, hh, mm = "00", ss = "00", frac = "0"] = mIso;
    const y = Number(yStr);
    const M = Number(MStr);
    const d = Number(dStr);
    if (M < 1 || M > 12) throw new Error(`Invalid month (1-12 expected): ${M}`);
    const dim = UTCDate.daysInMonth(y, M - 1);
    if (d < 1 || d > dim) throw new Error(`Invalid day-of-month: ${y}-${M}-${d} (month has ${dim} days)`);
    if (!(hh === "00" && mm === "00" && ss === "00" && /^0+$/.test(frac))) {
      throw new Error(`UTCDate timestamp not midnight UTC: ${s}`);
    }
    return UTCDate.fromYMD(y, M - 1, d);
  }

  /**
   * Safe parse that returns null instead of throwing on invalid input.
   * Accepts either 'YYYY-MM-DD' or a strict midnight ISO string.
   */
  static tryParse(s: string): UTCDate | null {
    try {
      return UTCDate.parse(s);
    } catch {
      return null;
    }
  }

  /**
   * Returns the current UTC calendar date (UTC midnight) based on the current instant.
   * Only the date portion of the current UTC timestamp is used.
   * Note: This can differ from the local (browser/server) calendar date if the local time zone
   * is behind or ahead of UTC. Example: 23:30 in New York (still previous local day) may already be the next day in UTC.
   */
  static today(): UTCDate {
    const now = new Date(Date.now());
    return UTCDate.fromYMD(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  }

  /**
   * The calendar day the user sees on their own clock. Date pickers (PrimeNG) mark and compare "today" with the
   * browser's local day, so their bounds and the defaults written into them must use this, not `today()`: east of UTC,
   * `today()` is still yesterday right after local midnight (west of UTC it is already tomorrow in the evening), and a
   * max bound of `today()` makes today unselectable.
   * Browser-only, because on the server "local" is the host's zone, which differs between dev machines and prod;
   * server code wants `BerlinDate.today()` (German business day) instead.
   * Reads `new Date()` directly instead of going through `DateTime`, which imports this file.
   */
  static localToday(): UTCDate {
    if (typeof window === "undefined") {
      throw new Error("UTCDate.localToday can only be used in a browser environment");
    }

    const now = new Date(Date.now());
    return UTCDate.fromYMD(now.getFullYear(), now.getMonth(), now.getDate());
  }

  /** Build from epoch millis (expects midnight UTC) */
  static fromEpochMillis(epochMillis: number): UTCDate {
    if (!Number.isFinite(epochMillis)) throw new Error("Invalid epochMillis");
    const d = new Date(epochMillis);
    if (d.getUTCHours() || d.getUTCMinutes() || d.getUTCSeconds() || d.getUTCMilliseconds()) {
      throw new Error("fromEpochMillis requires UTC midnight millis for UTCDate");
    }
    return UTCDate.fromYMD(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }

  /** Days in month (handles leap years) */
  static daysInMonth(year: number, monthIndex: number): number {
    return new Date(Date.UTC(year, monthIndex + 1, 0)).getUTCDate();
  }

  /** Smallest date (throws if empty) */
  static min(...dates: UTCDate[]): UTCDate {
    if (dates.length === 0) return undefined;
    return dates.reduce((m, d) => (d.epochMillisUTC() < m.epochMillisUTC() ? d : m));
  }

  /** Largest date (throws if empty) */
  static max(...dates: UTCDate[]): UTCDate {
    if (dates.length === 0) return undefined;
    return dates.reduce((m, d) => (d.epochMillisUTC() > m.epochMillisUTC() ? d : m));
  }

  /** Compare two UTCDate values: <0 if a<b, 0 if equal, >0 if a>b */
  static compare(a: UTCDate, b: UTCDate): number {
    return a.epochMillis - b.epochMillis;
  }

  private computeEpochMillis(): number {
    // If constructed via zero-arg path and later mutated by class-transformer (year/month/date changed
    // but cached epochMillis still equals placeholder), recompute on the fly.
    if (this.epochMillis === Date.UTC(0, 0, 1) && (this.year !== 0 || this.month !== 0 || this.date !== 1)) {
      return Date.UTC(this.year, this.month, this.date);
    }
    return this.epochMillis;
  }

  /** Epoch millis (UTC midnight) */
  epochMillisUTC(): number {
    return this.computeEpochMillis();
  }

  /** Alias for epochMillisUTC */
  toEpochMillis(): number {
    return this.computeEpochMillis();
  }

  /** Compatibility: Date-like getTime */
  getTime(): number {
    return this.computeEpochMillis();
  }

  /** Fresh native Date at UTC midnight */
  toDateAtUtcMidnight(): Date {
    return new Date(this.epochMillis);
  }

  toLocalDateAtMidnight(): Date {
    if (typeof window === "undefined")
      throw new Error("fromUTCToLocalDateAtMidnight can only be used in a browser environment");

    return new Date(this.year, this.month, this.date);
  }

  /** Alias for toDateAtUtcMidnight */
  toDate(): Date {
    return this.toDateAtUtcMidnight();
  }

  /** Year (full, e.g. 2025) */
  getFullYear(): number {
    return this.year;
  }

  /** Month index 0-11 */
  getMonth(): number {
    return this.month;
  }

  /** Day of month 1-31 */
  getDate(): number {
    return this.date;
  }

  /** Alias for getFullYear */
  getYear(): number {
    return this.getFullYear();
  }

  /** Alias for getMonth (explicit naming) */
  getMonthIndex(): number {
    return this.getMonth();
  }

  /** Alias for getDate with clearer intent */
  getDayOfMonth(): number {
    return this.getDate();
  }

  valueOf(): number {
    return this.computeEpochMillis();
  }

  [Symbol.toPrimitive](_hint: string): number {
    return this.epochMillis;
  }

  // Returns 'YYYY-MM-DD'
  toISODate(): string {
    return `${pad(this.year, 4)}-${pad(this.month + 1)}-${pad(this.date)}`;
  }

  // Always midnight UTC representation
  toISOString(): string {
    return `${this.toISODate()}T00:00:00.000Z`;
  }

  // JSON serialize as date-only string (stable & shorter)
  toJSON(): string {
    return this.toISODate();
  }

  /**
   * Returns a human-readable string representation of this UTCDate.
   * Format: "YYYY-MM-DD"
   *
   * @example
   * UTCDate.parse("2025-01-15").toString()
   * // => "2025-01-15"
   */
  toString(): string {
    return this.toISODate();
  }

  // Comparison helpers (semantic clarity)
  isEqual(other: UTCDate): boolean {
    return this.epochMillis === other.epochMillis;
  }

  isBefore(other: UTCDate): boolean {
    return this.epochMillis < other.epochMillis;
  }

  isAfter(other: UTCDate): boolean {
    return this.epochMillis > other.epochMillis;
  }

  /** Difference in whole days (other - this) */
  diffInDays(other: UTCDate): number {
    const msPerDay = 86_400_000;
    return Math.round((other.epochMillis - this.epochMillis) / msPerDay);
  }

  /** Add (or subtract) whole days */
  addDays(days: number): UTCDate {
    if (!Number.isFinite(days)) throw new Error(`Invalid days: ${days}`);
    const msPerDay = 86_400_000;
    const d = new Date(this.epochMillis + days * msPerDay);
    return UTCDate.fromYMD(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
  }

  /** Alias for addDays */
  plusDays(days: number): UTCDate {
    return this.addDays(days);
  }

  /** Subtract days */
  minusDays(days: number): UTCDate {
    return this.addDays(-days);
  }

  /** Set Day of month (returns new instance) */
  setDayOfMonth(dayOfMonth: number): UTCDate {
    const date = new Date(this.epochMillis);
    date.setUTCDate(dayOfMonth);
    return UTCDate.fromYMD(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }

  /** Set Month index (returns new instance) */
  setMonth(monthIndex: number): UTCDate {
    const date = new Date(this.epochMillis);
    date.setUTCMonth(monthIndex);
    return UTCDate.fromYMD(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }

  /** Set Year (returns new instance) */
  setYear(year: number): UTCDate {
    const date = new Date(this.epochMillis);
    date.setUTCFullYear(year);
    return UTCDate.fromYMD(date.getUTCFullYear(), date.getUTCMonth(), date.getUTCDate());
  }
}

/** Type guard for UTCDate */
export function isUTCDate(value: unknown): value is UTCDate {
  return !!value && typeof value === "object" && (value as any).constructor === UTCDate;
}

function pad(n: number, width = 2): string {
  const s = String(Math.abs(n));
  return s.length >= width ? s : "0".repeat(width - s.length) + s;
}
